import { useEffect, useState } from 'react';
import { Loader2, Activity, ChevronRight, AlertCircle, Bot, Sparkles, GitBranch, BookOpen } from 'lucide-react';

/**
 * Milestone D — Per-call replay timeline.
 *
 * Renders the structured trace from /api/dashboard/calls/{id}/replay so the
 * customer can see exactly which conversation nodes Ava traversed during a
 * live call, what intent she classified, and what the engine returned.
 *
 * Embedded in the existing Call detail drawer as a collapsible section.
 */
const KIND_META = {
  route_intent: { Icon: Activity,  color: 'text-blue-500',    label: 'Caller intent' },
  next_node:    { Icon: ChevronRight, color: 'text-purple-500', label: 'Engine routed' },
  engine_end:   { Icon: AlertCircle, color: 'text-amber-500',  label: 'Engine wrap-up' },
};

const NODE_KIND_ICON = {
  convo_say: Bot, convo_ask: Bot, convo_branch: GitBranch, convo_lookup_kb: BookOpen,
  convo_book: Sparkles, convo_transfer: Bot, convo_end: Bot,
};

export default function CallReplayPanel({ api, callId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!callId) return;
    setLoading(true); setErr(null);
    (async () => {
      try {
        const { data } = await api.get(`/dashboard/calls/${encodeURIComponent(callId)}/replay`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || 'Could not load replay.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, callId]);

  if (loading) {
    return (
      <div data-testid="call-replay-loading" className="flex items-center justify-center py-6">
        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg p-3 text-[12.5px] text-red-700 dark:text-red-300">
        {err}
      </div>
    );
  }

  const events = data?.events || [];
  const state = data?.state || {};

  if (events.length === 0) {
    return (
      <div data-testid="call-replay-empty" className="bg-gray-50 dark:bg-white/[0.03] border border-dashed border-gray-200 dark:border-white/10 rounded-xl p-4 text-center">
        <p className="text-xs text-gray-400 dark:text-slate-500">
          No conversation trace recorded for this call. The replay timeline only appears for calls that hit the live conversation engine.
        </p>
      </div>
    );
  }

  const violations = state?.guardrail_violations || [];

  return (
    <div data-testid="call-replay-panel" className="space-y-3">
      {/* Summary chip row */}
      <div className="flex flex-wrap items-center gap-2 text-[11.5px]">
        <span className="bg-gray-100 dark:bg-white/[0.05] text-gray-700 dark:text-slate-300 rounded-full px-2.5 py-1">
          Steps: <strong className="tabular-nums">{state?.step_count ?? events.filter(e => e.kind === 'route_intent').length}</strong>
        </span>
        {state?.last_intent && (
          <span className="bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 rounded-full px-2.5 py-1">
            Last intent: <strong>{state.last_intent}</strong>
          </span>
        )}
        {violations.length > 0 && (
          <span className="bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 rounded-full px-2.5 py-1">
            Guardrails fired: {violations.length}
          </span>
        )}
      </div>

      {/* Timeline */}
      <ol className="relative border-l border-gray-200 dark:border-white/[0.08] ml-2.5 pl-4 space-y-3">
        {events.map((ev, i) => {
          const meta = KIND_META[ev.kind] || { Icon: Activity, color: 'text-gray-400', label: ev.kind };
          const Icon = meta.Icon;
          const t = ev.ts ? new Date(ev.ts).toLocaleTimeString() : '';
          const p = ev.payload || {};
          const NodeIcon = p.kind ? NODE_KIND_ICON[p.kind] : null;
          return (
            <li key={i} data-testid={`replay-event-${i}`} className="relative">
              <span className={`absolute -left-[22px] top-1 w-4 h-4 rounded-full bg-white dark:bg-[#1a1a1c] border-2 border-current ${meta.color} flex items-center justify-center`}>
                <Icon className="w-2 h-2" />
              </span>
              <div className="bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-lg p-2.5">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className={`text-[11.5px] font-medium ${meta.color}`}>{meta.label}</p>
                  {t && <span className="text-[10.5px] text-gray-400 dark:text-slate-500 tabular-nums">{t}</span>}
                </div>
                {ev.kind === 'route_intent' && (
                  <p className="text-[12.5px] text-gray-800 dark:text-slate-200">
                    Ava classified intent as <strong>{p.intent || '(empty)'}</strong>
                    {p.from_node_id ? <> from node <code className="text-[11px] bg-gray-100 dark:bg-white/[0.06] px-1 rounded">{p.from_node_id.slice(0,8)}…</code></> : null}
                  </p>
                )}
                {ev.kind === 'next_node' && (
                  <div>
                    <p className="text-[12.5px] text-gray-800 dark:text-slate-200 flex items-center gap-1.5">
                      {NodeIcon && <NodeIcon className="w-3 h-3 text-purple-500" />}
                      <span>{p.label || p.kind || 'Next node'}</span>
                      {p.kind && <span className="text-[10.5px] uppercase tracking-wide text-gray-400 dark:text-slate-500">{p.kind}</span>}
                    </p>
                    {p.instruction_preview && (
                      <p className="text-[11.5px] text-gray-600 dark:text-slate-400 mt-1 leading-snug italic">
                        “{p.instruction_preview}{p.instruction_preview.length >= 200 ? '…' : ''}”
                      </p>
                    )}
                  </div>
                )}
                {ev.kind === 'engine_end' && (
                  <p className="text-[12.5px] text-amber-700 dark:text-amber-300">
                    Conversation ended ({p.reason || 'wrap-up'}).
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
